// Live sessions — going live, ending, and marking attendance.
//
// None of this was wired up before: the classroom's "Start session" button set a
// React state flag and nothing else, so no session was ever created, no student
// ever saw one, and no attendance was ever recorded. These are the endpoints that
// actually do it.

import { get, post, patch } from './client';
import { serverDateToDmy } from './adapters';

/**
 * "Nothing live right now" is an ERROR response, not an empty one: the middleware
 * raises 404 "No active session found for your group". That is an ordinary state —
 * most of the time no class is running — so it resolves to null instead of
 * throwing, the same way misc.js handles the endpoints that 404 on empty.
 *
 * The status is matched loosely because AppError's statusCode is unreliable
 * (see the note in client.js: httpStatus exports strings, so several "not found"
 * errors surface as 400).
 */
const noneLive = (status, payload) => status >= 400 && status < 500
  && /no active session|not started|not found/i.test(payload?.data?.message || payload?.message || '');

/**
 * One session row, as the UI reads it.
 *
 * `topicId` is what ties it to a topic the pages already hold, so the live banner
 * can name the topic without another request.
 */
const toUiSession = (s) => (s ? {
  id: String(s.sessionId ?? s.id ?? ''),
  topicId: String(s.topicId ?? ''),
  group: s.group || '',
  semester: s.semester || '',
  day: s.day || '',
  at: serverDateToDmy(s.dateAndTime),
  // The raw timestamp too: the attendance window is measured in hours, so the
  // banner needs more than the date.
  startedAt: s.dateAndTime || null,
  finished: !!s.finished,
} : null);

/**
 * POST /session/startSession — go live.
 *
 * `topicId` is the topic picked in the UI. The endpoint used to ignore it and
 * always take the group's most recently created topic, which made the picker on
 * screen decorative; it now honours it (and still falls back to that topic when
 * the field is omitted).
 *
 * The server sets the date, day and group itself from the clock and the token.
 */
export async function startSession(topicId) {
  const data = await post('/session/startSession', topicId ? { topicId: Number(topicId) } : {});
  return toUiSession(data);
}

// PATCH /session/endSession — marks the group's live session finished and clears
// it from the cache. 404s when nothing is live.
export const endSession = () => patch('/session/endSession');

/**
 * GET /session/getActiveSession — what is live for the caller's group, or null.
 *
 * Readable by BOTH roles: a student needs it to know their class has started, and
 * it resolves the group-then-'all' fallback, so a session started by the teacher
 * account reaches every group.
 */
export async function getActiveSession() {
  const data = await get('/session/getActiveSession', {
    isEmpty: noneLive,
    emptyValue: null,
  });
  return toUiSession(data?.activeSession || data);
}

/**
 * POST /session/attendSession — records the caller's attendance in whatever is
 * live. Idempotent for a student ("Re-attending this session"), and a no-op for an
 * admin, who is told they are entering rather than attending.
 *
 * The server enforces the window: same calendar day, and within 150 minutes of the
 * start. Outside it the call fails rather than back-dating attendance.
 */
export const attendSession = () => post('/session/attendSession');

/**
 * GET /session/getAllAttendanceForSession/:sessionId — who turned up.
 * Admin only, and the session must belong to their group.
 */
export async function getAttendance(sessionId) {
  const data = await get(`/session/getAllAttendanceForSession/${encodeURIComponent(sessionId)}`, {
    isEmpty: (status, payload) => status >= 400 && status < 500
      && /not found|no attendance/i.test(payload?.data?.message || payload?.message || ''),
    emptyValue: { attendanceRecords: [] },
  });
  const rows = Array.isArray(data?.attendanceRecords) ? data.attendanceRecords : [];
  return rows.map((r) => ({
    id: String(r?.attId ?? r?.id ?? ''),
    studentId: String(r?.studentId ?? ''),
    at: serverDateToDmy(r?.recordedAt),
  }));
}
